import type { OnlineHubCategory, OnlineHubPathway } from "@/content/online-courses/hub";

export type OnlineCourseVatMode = "vat_inclusive" | "ex_vat";

export type OnlineOptionTierContext = {
  hubCategory?: OnlineHubCategory;
  qualificationType?: "cpd" | "vtct";
  courseTitle?: string;
};

const SENSITIVE_TIER_TITLE_PATTERN =
  /\b(injectable|injection|filler|anti.wrinkle|profhilo|polynucleotide|lip filler|toxin|skin booster|mesotherapy|thread|tear trough|temple filler|complication|dissolv)\b/i;

export const usesSensitiveOnlineTierCopy = ({
  hubCategory,
  courseTitle = "",
}: OnlineOptionTierContext) =>
  hubCategory === "injectables-advanced" ||
  SENSITIVE_TIER_TITLE_PATTERN.test(courseTitle);

export const optionTierCardLabel = (
  option: Pick<OnlineCourseOption, "option_code">,
  context: OnlineOptionTierContext = {},
) => {
  const sensitive = usesSensitiveOnlineTierCopy(context);

  switch (option.option_code) {
    case "theory":
      return "Online course";
    case "case_study":
      return "Online course + tutor-reviewed case study";
    case "live":
      return sensitive
        ? "Online course + portfolio review + assessment"
        : "Online course + tutor review + live assessment";
    default:
      return "";
  }
};

export const optionTierSubmissionNote = (
  option: Pick<
    OnlineCourseOption,
    | "option_code"
    | "consultation_form_required"
    | "video_evidence_required"
    | "case_study_required"
    | "webinar_required"
    | "assessment_required"
  >,
  context: OnlineOptionTierContext = {},
) => {
  const sensitive = usesSensitiveOnlineTierCopy(context);

  if (option.option_code === "theory") {
    return null;
  }

  if (option.option_code === "case_study") {
    if (sensitive) {
      return "Submit structured portfolio evidence online for tutor review.";
    }

    const extras: string[] = [];
    if (option.consultation_form_required) {
      extras.push("consultation documentation");
    }
    if (option.video_evidence_required) {
      extras.push("supporting video evidence");
    }
    if (extras.length === 0) {
      return "Submit your case study portfolio online.";
    }
    return `Submit your case study portfolio online, including ${extras.join(
      " and ",
    )}.`;
  }

  if (option.option_code === "live") {
    if (sensitive) {
      return "Includes portfolio review plus scheduled online theory assessment.";
    }

    const parts = ["Includes everything in the tutor-reviewed tier"];
    if (option.webinar_required) {
      parts.push("a group webinar");
    }
    if (option.assessment_required) {
      parts.push("a 1:1 live assessment");
    }
    return `${parts.join(", plus ")}.`;
  }

  return null;
};

export const optionTierReviewNote = (
  option: Pick<OnlineCourseOption, "option_code" | "tutor_review_required">,
) => {
  if (
    option.option_code === "theory" ||
    !option.tutor_review_required
  ) {
    return null;
  }

  return "Tutor review and feedback typically within 5 working days of submission.";
};

export const optionTierCertificateNote = (
  option: Pick<OnlineCourseOption, "option_code">,
  context: OnlineOptionTierContext = {},
) => {
  const sensitive = usesSensitiveOnlineTierCopy(context);
  const isVtct = context.qualificationType === "vtct";

  if (option.option_code === "theory") {
    return isVtct
      ? "VTCT theory certificate on completion."
      : "CPD certificate confirming online theory completion.";
  }

  if (option.option_code === "case_study") {
    if (sensitive) {
      return "Certificate confirms online theory and portfolio review - not practical competence or treatment sign-off.";
    }

    return isVtct
      ? "Certificate confirms theory completion plus tutor-reviewed case study."
      : "CPD certificate confirming theory and tutor-reviewed case study completion.";
  }

  if (option.option_code === "live") {
    if (sensitive) {
      return "Certificate confirms theory, portfolio review and online theory assessment - not a substitute for practical training.";
    }

    return "Certificate confirms the full online pathway, including tutor review and live assessment where included.";
  }

  return null;
};

export const buildOptionTierContext = (
  course: OnlineOptionTierContext,
): OnlineOptionTierContext => ({
  hubCategory: course.hubCategory,
  qualificationType: course.qualificationType,
  courseTitle: course.courseTitle,
});

export const shouldHighlightPathwayTier = (
  optionCode: OnlineCourseOption["option_code"],
  highlightPathway?: OnlineHubPathway,
) => Boolean(highlightPathway && optionCode === highlightPathway);

export type OnlineCourseKitLine = {
  name: string;
  pricePence?: number | null;
  vatMode?: OnlineCourseVatMode | string;
  required?: boolean;
  autoAdd?: boolean;
  auto_add?: boolean;
  shippingRequired?: boolean;
  shipping_required?: boolean;
  quantity?: number;
  notes?: string;
};

export type OnlineCourseOption = {
  id: string;
  parent_course_slug: string;
  option_code: "theory" | "case_study" | "live";
  option_label: string;
  option_description?: string | null;
  price_pence: number;
  was_price_pence?: number | null;
  price_vat_mode: OnlineCourseVatMode;
  is_default: boolean;
  display_order: number;
  delivery_mode: string;
  certificate_release_mode: string;
  case_study_required: boolean;
  video_evidence_required: boolean;
  consultation_form_required: boolean;
  tutor_review_required: boolean;
  live_session_required: boolean;
  webinar_required: boolean;
  assessment_required: boolean;
  required_kits: OnlineCourseKitLine[];
  required_products: OnlineCourseKitLine[];
  checkout_notes?: string | null;
  learner_notes?: string | null;
};

/** Live assessment tier hidden from storefront until the pathway relaunches. */
export const ONLINE_STOREFRONT_HIDDEN_OPTION_CODES = ["live"] as const;

export const isStorefrontVisibleOnlineOption = (
  option: Pick<OnlineCourseOption, "option_code">,
) =>
  !ONLINE_STOREFRONT_HIDDEN_OPTION_CODES.includes(
    option.option_code as (typeof ONLINE_STOREFRONT_HIDDEN_OPTION_CODES)[number],
  );

export const filterStorefrontVisibleOnlineOptions = (
  options: OnlineCourseOption[],
): OnlineCourseOption[] => options.filter(isStorefrontVisibleOnlineOption);

export const formatPricePence = (pricePence: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(pricePence / 100);

export const vatLineAmountPence = (
  amountPence: number,
  vatMode: OnlineCourseVatMode,
) => {
  if (vatMode === "ex_vat") {
    return Math.round(amountPence * 1.2);
  }
  return amountPence;
};

export const sortOnlineCourseOptionsByPrice = (
  options: OnlineCourseOption[],
): OnlineCourseOption[] =>
  [...options].sort((a, b) => {
    if (a.price_pence !== b.price_pence) {
      return a.price_pence - b.price_pence;
    }

    if (a.display_order !== b.display_order) {
      return a.display_order - b.display_order;
    }

    return a.option_code.localeCompare(b.option_code);
  });

export const buildStripePriceData = ({
  option,
  courseTitle,
}: {
  option: Pick<
    OnlineCourseOption,
    | "id"
    | "price_pence"
    | "price_vat_mode"
    | "option_label"
    | "option_code"
    | "parent_course_slug"
  >;
  courseTitle: string;
}) => {
  const unitAmount = vatLineAmountPence(option.price_pence, option.price_vat_mode);

  return {
    currency: "gbp",
    unit_amount: unitAmount,
    product_data: {
      name: `${courseTitle} - ${option.option_label}`,
      metadata: {
        parent_course_slug: option.parent_course_slug,
        option_code: option.option_code,
        configuration_id: option.id,
      },
    },
    tax_behavior: "inclusive" as const,
  };
};

export const kitRequiresShipping = (kits: OnlineCourseKitLine[] = []) =>
  kits.some((kit) => Boolean(kit.shippingRequired ?? kit.shipping_required));

export const buildConfigSnapshot = (config: Record<string, unknown>) => ({
  ...config,
  snapshotted_at: new Date().toISOString(),
});

export const optionComparisonCopy = (
  option: OnlineCourseOption,
  context: OnlineOptionTierContext = {},
) => {
  const includes: string[] = [];
  const sensitive = usesSensitiveOnlineTierCopy(context);
  const certificateNote = optionTierCertificateNote(option, context);
  const submissionNote = optionTierSubmissionNote(option, context);
  const reviewNote = optionTierReviewNote(option);

  if (option.option_code === "theory") {
    includes.push("Step-by-step video demonstrations of the treatment");
    includes.push("On-demand video lessons - study at your own pace");
    if (certificateNote) {
      includes.push(certificateNote);
    }
    return includes;
  }

  includes.push("Everything in the online course, including video demonstrations");

  if (option.option_code === "case_study") {
    if (submissionNote) {
      includes.push(submissionNote);
    } else if (sensitive) {
      includes.push("Structured portfolio submitted online");
    } else {
      includes.push("Case study portfolio submitted online");
    }

    if (option.consultation_form_required) {
      includes.push("Consultation documentation");
    }
    if (option.video_evidence_required) {
      includes.push("Video evidence submission");
    }
    if (reviewNote) {
      includes.push(reviewNote);
    } else if (option.tutor_review_required) {
      includes.push("Tutor review before certification");
    }
    if (certificateNote) {
      includes.push(certificateNote);
    }
  }

  if (option.option_code === "live") {
    includes.push(
      sensitive
        ? "Everything in the portfolio review tier"
        : "Everything in the tutor-reviewed tier",
    );

    if (option.webinar_required) {
      includes.push("Group webinar session");
    }
    if (option.assessment_required) {
      includes.push(
        sensitive
          ? "Online theory assessment session"
          : "1:1 live assessment",
      );
    }
    if (reviewNote) {
      includes.push(reviewNote);
    }
    if (certificateNote) {
      includes.push(certificateNote);
    }
  }

  if (option.required_kits?.length) {
    includes.push("Optional training kits available");
  }

  return includes;
};

export const optionCardDescription = (
  option: OnlineCourseOption,
  courseTitle: string,
  context: OnlineOptionTierContext = {},
) => {
  if (option.option_description?.trim()) {
    return option.option_description.trim();
  }

  const sensitive = usesSensitiveOnlineTierCopy({
    ...context,
    courseTitle: context.courseTitle || courseTitle,
  });

  if (option.option_code === "theory") {
    return sensitive
      ? `Learn at your own pace with step-by-step video demonstrations. Ideal for a knowledge refresh without portfolio review.`
      : `Learn at your own pace with step-by-step video demonstrations of the treatment - the perfect starting point.`;
  }

  if (option.option_code === "case_study") {
    const parts = sensitive
      ? [
          `Add tutor-reviewed case study work for ${courseTitle} - knowledge-based evidence, not practical sign-off.`,
        ]
      : [`Go further with tutor-reviewed case study work for ${courseTitle}.`];

    if (option.tutor_review_required) {
      parts.push(
        sensitive
          ? "Includes online submission, tutor review, and certification wording that reflects case study completion."
          : "Includes case study submission and tutor review within around 5 working days.",
      );
    }
    parts.push(
      "Your certificate confirms tutor-reviewed case study work - stronger evidence of your training when employers or insurers ask.",
    );
    if (option.required_kits?.length) {
      parts.push("Add a training kit if you need products supplied.");
    }
    return parts.join(" ");
  }

  if (option.option_code === "live") {
    const liveParts = sensitive
      ? [
          `The fullest online knowledge pathway for ${courseTitle}, with portfolio review plus scheduled theory assessment support.`,
        ]
      : [
          `The full pathway for ${courseTitle}, with online learning plus scheduled live sessions.`,
        ];

    if (option.webinar_required && option.assessment_required) {
      liveParts.push(
        sensitive
          ? "Includes a group webinar and an online theory assessment."
          : "Includes a group webinar and a 1:1 assessment.",
      );
    } else if (option.webinar_required) {
      liveParts.push("Includes a group webinar session.");
    } else if (option.assessment_required) {
      liveParts.push(
        sensitive
          ? "Includes an online theory assessment session."
          : "Includes a 1:1 assessment session.",
      );
    }
    return liveParts.join(" ");
  }

  return option.learner_notes?.trim() || "";
};

export const optionCardEyebrow = (
  option: OnlineCourseOption,
  context: OnlineOptionTierContext = {},
) => {
  const sensitive = usesSensitiveOnlineTierCopy(context);

  switch (option.option_code) {
    case "theory":
      return "Best starting point";
    case "case_study":
      return sensitive ? "Portfolio review pathway" : "Tutor-reviewed pathway";
    case "live":
      return sensitive ? "Includes theory assessment" : "Includes live sessions";
    default:
      return "";
  }
};

export const shouldShowOptionCardEyebrow = (
  option: OnlineCourseOption,
  context: OnlineOptionTierContext = {},
) => {
  const eyebrow = optionCardEyebrow(option, context).trim();
  if (!eyebrow) {
    return false;
  }

  return (
    eyebrow.toLowerCase() !== option.option_label.trim().toLowerCase()
  );
};

export function cardPriceLabel(
  options: OnlineCourseOption[],
  fallbackPricePence: number,
) {
  if (options.length === 0) {
    return formatPricePence(fallbackPricePence);
  }

  const prices = options.map((option) => option.price_pence);
  const min = Math.min(...prices);
  const max = Math.max(...prices);

  if (min === max) {
    return formatPricePence(min);
  }

  return `From ${formatPricePence(min)}`;
}

export function courseCardNeedsOptionFlow(
  optionsManaged: boolean,
  options: OnlineCourseOption[],
) {
  if (!optionsManaged || options.length === 0) {
    return false;
  }

  if (options.length > 1) {
    return true;
  }

  return (options[0].required_kits || []).some((kit) => {
    const price = kit.pricePence ?? (kit as { price_pence?: number }).price_pence ?? 0;
    return price > 0;
  });
}
